import { useVideoCreatorStore } from '@src/stores-v2';
import { findLayoutRelatedElements } from '@src/utility/stackedLayout';
import { mapToElementState } from '@src/videoTranscriptionProcessor/utils';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useState } from 'react';

import type { Element } from '../../editor';
import type { ElementState } from '@src/renderer/ElementState';

type ReturnType = {
  activeTimelineVideoElement: ElementState;
  hiddenParentElement: ElementState | null;
  isLayoutComposition: boolean;
  childVideoElement: ElementState | null;
  displayElement: ElementState;
  sourceUrl: string;
  xValue: number;
  yValue: number;
  updateVideoProperty: (propertyUpdates: Record<string, any>) => Promise<void>;
  updateTimelineVideoProperty: (
    propertyUpdates: Record<string, any>,
  ) => Promise<void>;
  modifyPropertyWithSlider: (propertyName: string, newValue: number) => void;
  handleChangeScale: (value: number) => void;
};

export default function useLayoutAwareVideoSettingsState({
  activeElement,
}: {
  activeElement: ElementState;
}): ReturnType {
  const videoCreator = useVideoCreatorStore();
  const videoSourceStore = videoCreator.rootStore.videoSourceStore;
  const { editorStore } = videoCreator.rootStore;

  const [hiddenParentElement, setHiddenParentElement] =
    useState<ElementState | null>(null);
  const [isLayoutComposition, setIsLayoutComposition] =
    useState<boolean>(false);
  const [childVideoElement, setChildVideoElement] =
    useState<ElementState | null>(null);
  // Keep track of slider values to prevent UI from snapping
  const [sliderValues, setSliderValues] = useState<{
    x: number | null;
    y: number | null;
  }>({
    x: null,
    y: null,
  });

  const expandAlignmentProperties = (
    propertyUpdates: Record<string, any>,
  ): Record<string, any> => {
    for (const key in propertyUpdates) {
      if (key === 'x') {
        propertyUpdates.x_anchor = propertyUpdates[key];
        propertyUpdates.x_alignment = propertyUpdates[key];
      }
      if (key === 'y') {
        propertyUpdates.y_anchor = propertyUpdates[key];
        propertyUpdates.y_alignment = propertyUpdates[key];
      }
    }
    return propertyUpdates;
  };

  const commandUpdateElementDebounced = useCallback(
    debounce((elementId: string, propertyUpdates: Record<string, any>) => {
      editorStore.editor.commandUpdateElement(elementId, propertyUpdates);
    }, 250),
    [editorStore.editor],
  );

  const commandUpdateMultipleElementsDebounced = useCallback(
    debounce((updatesMap: Map<string, Record<string, Partial<Element>>>) => {
      editorStore.editor.commandUpdateMultipleElements(updatesMap);
    }, 250),
    [editorStore.editor],
  );

  // Helper function to update video element properties using headless editor or fallback to legacy
  const updateVideoProperty = async (propertyUpdates: Record<string, any>) => {
    const elementToModify = childVideoElement || activeElement;
    if (
      editorStore?.headlessEnabled &&
      editorStore?.enableHeadlessEditorExperimental &&
      editorStore?.editor
    ) {
      // Use headless editor UpdateElementCommand
      commandUpdateElementDebounced(
        elementToModify.source.id,
        expandAlignmentProperties(propertyUpdates),
      );
    } else {
      // Fallback to legacy method
      Object.entries(propertyUpdates).forEach(([key, value]) => {
        modifyPropertyDebounced(key, value, elementToModify.source.id);
      });
    }
  };

  const updateTimelineVideoProperty = async (
    propertyUpdates: Record<string, any>,
  ) => {
    const elementToModify = getActiveTimelineVideoElement();
    if (
      editorStore?.headlessEnabled &&
      editorStore?.enableHeadlessEditorExperimental &&
      editorStore?.editor
    ) {
      const layoutInfo = findLayoutRelatedElements(
        videoCreator,
        elementToModify.source.id,
      );
      if (layoutInfo.isLayoutChild || layoutInfo.isHiddenParent) {
        const idsToUpdate = [
          layoutInfo.hiddenParentId,
          ...layoutInfo.layoutChildrenIds,
        ].filter((id): id is string => !!id);
        const updatesMap = new Map<string, Record<string, any>>();
        for (const id of idsToUpdate) {
          updatesMap.set(id, {
            ...expandAlignmentProperties(propertyUpdates),
          });
        }
        commandUpdateMultipleElementsDebounced(updatesMap);
      } else {
        commandUpdateElementDebounced(
          elementToModify.source.id,
          expandAlignmentProperties(propertyUpdates),
        );
      }
    } else {
      // Fallback to legacy method
      Object.entries(propertyUpdates).forEach(([key, value]) => {
        modifyPropertyDebounced(key, value, elementToModify.source.id);
      });
    }
  };

  // Function to update slider values from the current element properties
  // Using a function in the setState to avoid dependency on sliderValues
  const updateSliderValuesFromElement = useCallback(() => {
    const element = childVideoElement || activeElement;
    if (element && element.source) {
      setSliderValues(prevValues => {
        const newValues = { ...prevValues };

        // For X alignment, prefer x_alignment if available, then x
        if (element.source.x_alignment !== undefined) {
          const xString = element.source.x_alignment.toString();
          const xVal = parseFloat(xString);
          if (!isNaN(xVal)) newValues.x = xVal;
        } else if (element.source.x !== undefined) {
          const xString = element.source.x.toString();
          const xVal = parseFloat(xString);
          if (!isNaN(xVal)) newValues.x = xVal;
        } else {
          newValues.x = 50; // Default value
        }

        // For Y alignment, prefer y_alignment if available, then y
        if (element.source.y_alignment !== undefined) {
          const yString = element.source.y_alignment.toString();
          const yVal = parseFloat(yString);
          if (!isNaN(yVal)) newValues.y = yVal;
        } else if (element.source.y !== undefined) {
          const yString = element.source.y.toString();
          const yVal = parseFloat(yString);
          if (!isNaN(yVal)) newValues.y = yVal;
        } else {
          newValues.y = 50; // Default value
        }

        return newValues;
      });
    }
  }, [(childVideoElement || activeElement)?.source]);

  // Update slider values when the child/active video element changes
  useEffect(() => {
    updateSliderValuesFromElement();
  }, [updateSliderValuesFromElement]);

  // Modified function to immediately update the UI and then debounce the API call
  const modifyPropertyWithSlider = (propertyName: string, newValue: number) => {
    // Update local state immediately for smooth UI
    if (propertyName === 'x') {
      setSliderValues(prev => ({ ...prev, x: newValue }));
    } else if (propertyName === 'y') {
      setSliderValues(prev => ({ ...prev, y: newValue }));
    }

    // Convert to percentage string (without spaces)
    const propertyValue = `${newValue}%`;

    updateVideoProperty({ [propertyName]: propertyValue });
  };

  const modifyPropertyDebounced = useCallback(
    debounce(
      async (propertyName: string, propertyValue: any, elementId: string) => {
        const actionLabel = `changing video ${propertyName}`;
        const modifications: Record<string, string> = {};

        // If changing x or y alignment, also update the related properties to match
        if (propertyName === 'x') {
          // Set all related x-position values with percentage strings to ensure they stay in sync
          modifications[`${elementId}.x_anchor`] = propertyValue;
          modifications[`${elementId}.x`] = propertyValue;
          modifications[`${elementId}.x_alignment`] = propertyValue;
        } else if (propertyName === 'y') {
          // Set all related y-position values with percentage strings to ensure they stay in sync
          modifications[`${elementId}.y_anchor`] = propertyValue;
          modifications[`${elementId}.y`] = propertyValue;
          modifications[`${elementId}.y_alignment`] = propertyValue;
        } else {
          // For other properties that don't need syncing
          modifications[`${elementId}.${propertyName}`] = propertyValue;
        }

        await videoCreator.applyVideoStateModifications(
          modifications,
          true,
          actionLabel,
        );
      },
      250,
    ),
    [videoCreator],
  );

  const handleChangeScale = debounce(async (value: number) => {
    const actionLabel = 'changing video scale';
    const elementToModify = childVideoElement || activeElement;
    const source = elementToModify?.source;
    if (!source) return;

    // Parse current values (all as percentages)
    const parsePercent = (v: unknown, fallback: number) => {
      if (v === undefined || v === null) return fallback;
      if (typeof v === 'string' && v.endsWith('%')) return parseFloat(v);
      if (typeof v === 'number') return v;
      return fallback;
    };

    // Get current width/height, x/y, x_anchor/y_anchor
    const width = parsePercent(source.width, 100);
    const height = parsePercent(source.height, 100);
    const x = parsePercent(source.x, 50);
    const y = parsePercent(source.y, 50);
    const x_anchor = parsePercent(source.x_anchor ?? source.x_alignment, 50);
    const y_anchor = parsePercent(source.y_anchor ?? source.y_alignment, 50);

    // Calculate the visual center in composition coordinates
    const centerX = x + width * (0.5 - x_anchor / 100);
    const centerY = y + height * (0.5 - y_anchor / 100);

    // New width/height
    const newWidth = value;
    const newHeight = value;

    // To keep the center fixed, solve for new x/y:
    // centerX = newX + (newWidth * (0.5 - x_anchor/100))
    // => newX = centerX - newWidth * (0.5 - x_anchor/100)
    // same for y
    const newX = centerX - newWidth * (0.5 - x_anchor / 100);
    const newY = centerY - newHeight * (0.5 - y_anchor / 100);

    // Only update width, height, x, y. Do not touch alignment/anchor properties.
    const updates: Record<string, string> = {
      width: `${newWidth}%`,
      height: `${newHeight}%`,
      x: `${newX}%`,
      y: `${newY}%`,
    };

    if (
      editorStore?.headlessEnabled &&
      editorStore?.enableHeadlessEditorExperimental
    ) {
      commandUpdateElementDebounced(elementToModify.source.id, updates);
    } else {
      const id = source.id;
      const legacyUpdates: Record<string, string> = {};
      Object.entries(updates).forEach(([key, value]) => {
        legacyUpdates[`${id}.${key}`] = value;
      });
      await videoCreator.applyVideoStateModifications(
        legacyUpdates,
        true,
        actionLabel,
      );
    }
  }, 250);

  useEffect(() => {
    // Check if current element is a composition element part of a layout
    const elementId = activeElement.source.id;
    const allElements: Record<string, any>[] =
      videoSourceStore.source.elements ?? [];

    // Handle composition elements
    if (activeElement.source.type === 'composition') {
      // Find the child video element inside this composition
      if (activeElement.elements && activeElement.elements.length > 0) {
        const videoChild = activeElement.elements.find(
          e => e.source.type === 'video',
        );
        if (videoChild) {
          setChildVideoElement(videoChild);
        }
      }

      const layoutInfo = findLayoutRelatedElements(videoCreator, elementId);
      // Check if it's a layout composition
      if (layoutInfo.isLayoutChild) {
        setIsLayoutComposition(true);
        // This is a layout composition (top or bottom), get the hidden parent element
        const hiddenParent = allElements.find(
          e => e.id === layoutInfo.hiddenParentId,
        );
        if (hiddenParent) {
          setHiddenParentElement(
            mapToElementState(hiddenParent) as ElementState,
          );
        }
      } else {
        setIsLayoutComposition(false);
        setHiddenParentElement(null);
      }
    } else {
      // Handle direct video elements (not compositions)

      // Can be part of stacked layout as inner video of top/child composition
      const childComposition =
        videoCreator.stackedLayoutManager.getLayoutElementFor(activeElement);
      if (childComposition) {
        // This is a layout composition that contains active element
        setIsLayoutComposition(true);
        setChildVideoElement(activeElement);
        const layoutInfo = findLayoutRelatedElements(
          videoCreator,
          childComposition.source.id,
        );
        const hiddenParent = allElements.find(
          e => e.id === layoutInfo.hiddenParentId,
        );
        if (hiddenParent) {
          setHiddenParentElement(
            mapToElementState(hiddenParent) as ElementState,
          );
        }
        return;
      }

      // Not part of stacked layout
      setIsLayoutComposition(false);
      setHiddenParentElement(null);
      setChildVideoElement(null);
    }
  }, [activeElement, videoCreator.currentVideo?.extraElementData]);

  // Determine which element to use for different properties
  const displayElement = childVideoElement || activeElement;

  // Update slider values directly based on current state
  // Do this in render to ensure it's always up-to-date
  const activeElementForSliders = childVideoElement || activeElement;

  // Set x value directly from the source
  const xValue = (() => {
    if (!activeElementForSliders?.source) return 50;

    if (sliderValues.x != null) {
      return sliderValues.x;
    }

    // Try x_alignment first
    if (activeElementForSliders.source.x_alignment !== undefined) {
      const val = parseFloat(
        activeElementForSliders.source.x_alignment.toString(),
      );
      if (!isNaN(val)) return val;
    }

    // Then try x
    if (activeElementForSliders.source.x !== undefined) {
      const val = parseFloat(activeElementForSliders.source.x.toString());
      if (!isNaN(val)) return val;
    }

    return 50; // Default
  })();

  // Set y value directly from the source
  const yValue = (() => {
    if (!activeElementForSliders?.source) return 50;

    if (sliderValues.y != null) {
      return sliderValues.y;
    }

    // Try y_alignment first
    if (activeElementForSliders.source.y_alignment !== undefined) {
      const val = parseFloat(
        activeElementForSliders.source.y_alignment.toString(),
      );
      if (!isNaN(val)) return val;
    }

    // Then try y
    if (activeElementForSliders.source.y !== undefined) {
      const val = parseFloat(activeElementForSliders.source.y.toString());
      if (!isNaN(val)) return val;
    }

    return 50; // Default
  })();

  const sourceUrl = displayElement.source.source;

  return {
    activeTimelineVideoElement: getActiveTimelineVideoElement(),
    hiddenParentElement,
    isLayoutComposition,
    childVideoElement,
    displayElement,
    sourceUrl,
    xValue,
    yValue,
    updateVideoProperty,
    updateTimelineVideoProperty,
    modifyPropertyWithSlider,
    handleChangeScale,
  };

  function getActiveTimelineVideoElement() {
    return isLayoutComposition && hiddenParentElement
      ? hiddenParentElement
      : activeElement;
  }
}
